import React, { useEffect, useState } from 'react';
import { Button, showPopupMessage } from 'components';
import Store from 'store';
import { TOnlineStore } from 'type';

export const SyncEtsyOrdersButton = ({ resellerId }: { resellerId: string }) => {
  const [etsyStores, setEtsyStores] = useState<TOnlineStore[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (!resellerId || resellerId === 'all') return;
    Store.useShopStore.getState().getAll(resellerId).then((stores) => {
      const list = (Array.isArray(stores) ? stores : []).filter(
        (store) => store.type === 'etsy' && store.data?.etsyAccessToken && store.data?.etsyRefreshToken
      );
      setEtsyStores(list);
    });
  }, [resellerId]);

  const syncEtsyOrders = async () => {
    if (!etsyStores.length || isSyncing) return;
    setIsSyncing(true);
    try {
      let totalSynced = 0;
      let totalMissing = 0;
      const errors: string[] = [];

      for (const store of etsyStores) {
        const res = await Store.Api.Shop.syncEtsyOrders({ storeId: store.id });
        if (res.data?.error) {
          errors.push(`${store.name || store.id}: ${res.data.error}`);
          continue;
        }
        const { syncedCount = 0, missingCount = 0 } = res.data?.data || {};
        totalSynced += syncedCount;
        totalMissing += missingCount;
      }

      if (errors.length) {
        throw new Error(errors.join('\n'));
      }

      showPopupMessage({
        title: '',
        content: totalSynced > 0
          ? `Synced ${totalSynced} missing Etsy order(s).`
          : totalMissing > 0
            ? `Found ${totalMissing} missing order(s) but none could be synced.`
            : 'No missing Etsy orders found in the last 7 days.',
        buttonOkText: 'OK',
        typeHighlight: totalSynced > 0 ? 'success' : 'info',
        contentHighlight: totalSynced > 0 ? 'Orders synced' : 'Sync complete',
      });
    } catch (error) {
      showPopupMessage({
        title: '',
        content: error.message || String(error),
        buttonOkText: 'OK',
        typeHighlight: 'danger',
        contentHighlight: 'Error',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (!etsyStores.length) return null;

  return (
    <Button
      text="Sync Etsy orders"
      outline
      borderRadius={15}
      height={32}
      mr1
      isLoading={isSyncing}
      onPress={syncEtsyOrders}
    />
  );
};
